import React from 'react';

import { useRouter } from 'next/router';

import {
  ArrowForward,
  CropFree,
  FavoriteBorder,
  Favorite,
  Grass,
  LocalFireDepartment,
  LocationOnOutlined,
} from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
} from '@mui/material';
import { track } from '@klokhuis/core/Analytics/libs/analytics-lib';
import { AnalyticsEvent } from '@klokhuis/core/Analytics/types';
import {
  Listing,
  ListingStatus as ListingStatusEnum,
  PropertyTypeID,
} from '@klokhuis/core/ApiClient/Klokhuis';
import { AppServicesContext } from '@klokhuis/core/AppServices';
import { AuthContext } from '@klokhuis/core/Auth/AuthContext';
import { reportError } from '@klokhuis/core/Errors';
import { useIntl } from '@klokhuis/core/Intl';
import { Locale } from '@klokhuis/core/Intl/types';
import { logger } from '@klokhuis/core/Logger';
import { ROUTES } from '@klokhuis/core/Routing';
import { NavLink } from '@klokhuis/core/Routing/NavLink';
import {
  PropertyTypeIdPluralTranslationsTrimmed,
  PropertyTypeIDTranslations,
  UnitTypeTranslations,
} from '@klokhuis/modules/Properties/translations';
import {
  ListingChipLabels,
  ListingStatus,
} from '@klokhuis/modules/Properties/types';
import { listingTypeBadgeIcon } from '@klokhuis/modules/Properties/utilities/listingTypeBadgeIcons';

import fallbackImage from '@klokhuis/assets/fallback_image_1.png';

import { ListingChip } from './components/ListingChip/ListingChip';
import styles from './ListingCard.module.css';

export interface ListingCardProps extends Partial<Listing> {
  listingId: number;
  thumbnailUrl: string;
  fullPreviewImageUrl: string;
  isTopAdvert: boolean;
  streetName: string;
  cityName: string;
  areaOrLandmark?: string;
  isFavorite: boolean;
  surfaceArea?: number;
  plotArea?: number; // Updated to match usage
  plotAreaUnit?: any;
  listingType: PropertyTypeID;
  salePrice: number;
  isPriceNegotiable: boolean;
  listingStatus?: ListingStatusEnum;
  /** Pass true for above-the-fold cards to hint the browser to load eagerly (improves LCP) */
  priority?: boolean;
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  const img = e.target as HTMLImageElement;
  img.onerror = null;
  img.src = fallbackImage.src;
};

const ListingCardInternal = ({
  listingId,
  thumbnailUrl,
  fullPreviewImageUrl,
  isTopAdvert,
  streetName,
  cityName,
  areaOrLandmark,
  isFavorite,
  listingType,
  salePrice,
  plotArea,
  plotAreaUnit,
  listingStatus,
  priority = false,
}: ListingCardProps) => {
  const [isFavoriteListing, setIsFavorite] = React.useState(isFavorite);

  const { isAuthenticated } = AuthContext.useContext();
  const { T, locale } = useIntl();
  plotAreaUnit = T(UnitTypeTranslations[plotAreaUnit || ''] || '');
  const { apiClient } = AppServicesContext.useContext();
  const router = useRouter();

  const listingImageUrl = thumbnailUrl || fullPreviewImageUrl;

  const ListingTypeBadgeIcon = listingTypeBadgeIcon[listingType] ?? Grass;

  const listingTypeTranslation = T(
    PropertyTypeIdPluralTranslationsTrimmed[listingType],
  );

  const listingImageTitle = T(
    'listings_page.listing_card.image_title',
    listingTypeTranslation,
    salePrice,
    streetName,
    cityName,
  );

  const trackListingClick = React.useCallback(() => {
    const listingClickPayload = {
      citysearch: cityName,
      affiliate: 'none',
    };

    track(AnalyticsEvent.SearchListingClicked, listingClickPayload);
  }, [cityName]);

  const handleFavoriteClick = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isAuthenticated) {
        window.location.assign(ROUTES.SignupSignin.url({}));
        return;
      }

      setIsFavorite(!isFavoriteListing);

      try {
        const result = await apiClient.favorite.toggleFavorite({ listingId });
        setIsFavorite(result.favorited);
      } catch (error) {
        setIsFavorite(isFavoriteListing);
        reportError(error as Error, {
          extra: {
            context: '`Listing Search Page - Failed to add listing as favorite',
            metaData: { error },
          },
        });
        logger.error(
          `Listing Search Page - Failed to add listing as favorite: ${error}`,
        );
      }
    },
    [
      isAuthenticated,
      isFavoriteListing,
      router,
      locale,
      apiClient.favorite,
      listingId,
    ],
  );

  const listingTypeUrlSegment = listingTypeTranslation.toLocaleLowerCase();
  const normalizedCityName = cityName
    ? cityName.split(',').map(s => s.replace(/^\s+/, '')).join(',')
    : '';
  const normalizedStreetName = streetName
    ? streetName.split(',').map(s => s.replace(/^\s+/, '')).join(',')
    : '';
  const listingPageUrl = `${
    locale === Locale.en ? '/en/for-sale' : '/for-sale'
  }/${listingTypeUrlSegment}-${
    normalizedCityName && normalizedCityName.trimStart().trim().toLowerCase().replaceAll(' ', '-')
  }/${
    normalizedStreetName && normalizedStreetName.trimStart().trim().toLowerCase().replaceAll(' ', '-')
  }/${listingTypeUrlSegment}-${listingId}`;

  return (
    <Card
      className={styles.root}
      component={NavLink.Native}
      underline="none"
      href={listingPageUrl}
      target="_blank"
      onClick={trackListingClick}
    >
      <div className={styles.imageContainer}>
        <img
          className={styles.media}
          src={listingImageUrl || fallbackImage.src}
          alt={listingImageTitle}
          title={listingImageTitle}
          loading={priority ? 'eager' : 'lazy'}
          onError={handleImageError}
        />

        <div className={styles.badgesContainer}>
          {/* Hardcoded NEW badge for TopAd for demo purposes */}
          {isTopAdvert && (
            <div className={styles.badgeNew}>
              <Typography variant="caption">NEW</Typography>
            </div>
          )}
          {/* Hardcoded Hot Price for demo if needed, or based on logic */}
          {isTopAdvert && (
            <div className={styles.badgeHot} >
              <LocalFireDepartment  color='primary' />
              <Typography variant="caption" color="primary">Hot Price</Typography>
            </div>
          )}
          {listingStatus === ListingStatusEnum.SOLD && (
            <ListingChip label={ListingChipLabels.Sold} />
          )}
          {listingStatus === ListingStatusEnum.UNDER_NEGOTIATION && (
            <ListingChip label={ListingChipLabels.UnderNegotiation} />
          )}
        </div>

        <IconButton
          aria-label="add to favorites"
          className={styles.favoriteBtn}
          onClick={event => handleFavoriteClick(event)}
        >
          {isFavoriteListing ? (
            <Favorite fontSize="small" className={styles.favoriteIconActive} />
          ) : (
            <FavoriteBorder fontSize="small" />
          )}
        </IconButton>

        <div className={styles.plantIconContainer}>
          <ListingTypeBadgeIcon fontSize="small" />
        </div>
      </div>

      <CardContent className={styles.cardContent}>
        <Typography variant="h6" className={styles.cardTitle}>
          {T(PropertyTypeIDTranslations[listingType]) ?? listingType}
        </Typography>

        <div className={styles.infoRow}>
          <LocationOnOutlined className={styles.infoIcon} />
          <Typography variant="subtitle2" className={styles.infoText} noWrap>
            {`${cityName} ${streetName ? `, ${areaOrLandmark}` : ''}`}
          </Typography>
        </div>

        {/* {plotArea && ( */}
        <div className={styles.infoRow}>
          <CropFree className={styles.infoIcon} />
          <Typography variant="subtitle2" noWrap>
            {plotArea || 'N/A'} { plotArea ? plotAreaUnit : '' }
          </Typography>
        </div>
        {/* )} */}

        <div className={styles.footerRow}>
          <div className={styles.priceSection}>
            <Typography variant="body4" className={styles.priceLabel}>
              Price
            </Typography>
            <Typography variant="subtitle2" className={styles.priceValue}>
              {salePrice > 99999
                ? `₹${(salePrice / 100000).toFixed(2)} Lakh`
                : `₹${salePrice}`}
              <span className={styles.priceUnit}></span>
            </Typography>
          </div>

          <Button
            variant="outlined"
            className={styles.viewDetailsBtn}
            endIcon={<ArrowForward fontSize="small" />}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
export const ListingCard = React.memo(ListingCardInternal);
